import React, { memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { PNRInputField } from "@swiggy-private/dls-web-recipe-common-web/dist/components/PNRInputField";
import { IIrctcNonPnrHalfcardData } from "@swiggy-private/cart-types";
import {
    IRCTC_NON_PNR_HALF_CARD_DEFAULT_TEXT,
    IRCTC_NON_PNR_HALF_CARD_TEST_ID,
    PNR_INPUT_LIMIT,
    PNR_INPUT_LIMIT_ERROR,
} from "./constants";

import { ContentContainer, PNRInputFieldContainer, TextWrapper } from "./style";
import { getBridgeSdk, useKeyboardState, AnalyticsContext } from "@swiggy-private/checkout-core";

interface IrctcNonPnrHalfcardProps {
    onPnrComplete: (pnr: string) => void | Promise<unknown>;
    isHalfCardOpen: boolean;
    pnrHalfcardData: IIrctcNonPnrHalfcardData;
    analyticsCategory: string;
    analyticsVerifyAction: string;
    isOcrFlowEnabled: boolean;
    onClose?: () => void;
}

const IrctcNonPnrHalfcard = ({
    onPnrComplete,
    isHalfCardOpen,
    pnrHalfcardData,
    analyticsCategory,
    analyticsVerifyAction,
    isOcrFlowEnabled,
    onClose,
}: IrctcNonPnrHalfcardProps) => {
    const title = pnrHalfcardData?.title;
    const subtitle = pnrHalfcardData?.sub_title;
    const inputPlaceholder = pnrHalfcardData?.input_placeholder;

    const [pnr, setPnr] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { clickEvent } = useContext(AnalyticsContext);
    const sdk = getBridgeSdk();
    const inputRef = useRef<HTMLDivElement>(null);

    useKeyboardState(inputRef);
    const [, setErrorText] = useState("");
    const [, setIsVerifyButtonClicked] = useState(false);

    const titleRef = useRef(title);
    const subtitleRef = useRef(subtitle);
    if (title) {
        titleRef.current = title;
    }
    if (subtitle) {
        subtitleRef.current = subtitle;
    }

    const textFieldConfig = useMemo(
        () => ({
            label: inputPlaceholder ?? IRCTC_NON_PNR_HALF_CARD_DEFAULT_TEXT.inputPlaceholder,
            hintText: "",
            inputLimit: PNR_INPUT_LIMIT,
            inputLimitError: PNR_INPUT_LIMIT_ERROR,
        }),
        [inputPlaceholder],
    );

    const handleFetchPnrDetails = useCallback(
        async (value: string) => {
            if (analyticsCategory && analyticsVerifyAction) {
                clickEvent({
                    category: analyticsCategory,
                    action: analyticsVerifyAction,
                    label: value,
                });
            }
            setIsLoading(true);
            try {
                await onPnrComplete(value);
            } finally {
                setIsLoading(false);
            }
        },
        [onPnrComplete, clickEvent, analyticsCategory, analyticsVerifyAction],
    );

    return (
        <HalfCard isOpen={isHalfCardOpen} variant="v3" onClose={onClose}>
            <ContentContainer data-testid={IRCTC_NON_PNR_HALF_CARD_TEST_ID}>
                <TextWrapper>
                    <Text variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                        {titleRef.current ?? IRCTC_NON_PNR_HALF_CARD_DEFAULT_TEXT.title}
                    </Text>
                    <Text variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                        {subtitleRef.current ?? IRCTC_NON_PNR_HALF_CARD_DEFAULT_TEXT.subtitle}
                    </Text>
                </TextWrapper>
                <PNRInputFieldContainer ref={inputRef}>
                    <PNRInputField
                        pnr={pnr}
                        setPnr={setPnr}
                        apiCall={{ loading: isLoading }}
                        fetchPnrDetails={handleFetchPnrDetails}
                        isTextFieldDisabled={false}
                        errorText={""}
                        setErrorText={setErrorText}
                        setIsVerifyButtonClicked={setIsVerifyButtonClicked}
                        textFieldConfig={textFieldConfig}
                        isOcrFlowEnabled={isOcrFlowEnabled}
                        sdk={sdk as unknown as { scanPnr: () => Promise<{ pnr?: string }> }}
                    />
                </PNRInputFieldContainer>
            </ContentContainer>
        </HalfCard>
    );
};

export default memo(IrctcNonPnrHalfcard);
